// /en/visa-compliance — EN hub for TM30, 90-day report, re-entry, overstay, TDAC, etc.
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ComplianceCategory } from "@/data/visa-compliance-topics";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { ShieldCheck, Phone, MessageCircle, ArrowRight, Sparkles, AlertTriangle, FileText, Clock, Stamp } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/visa-compliance/")({
    loader: async () => {
    const { COMPLIANCE_TOPICS, COMPLIANCE_CATEGORY_LABELS_EN } = await import("@/data/visa-compliance-topics");
    return { __d: { COMPLIANCE_TOPICS, COMPLIANCE_CATEGORY_LABELS_EN } };
  },
head: ({ loaderData }) => {
    const { COMPLIANCE_TOPICS, COMPLIANCE_CATEGORY_LABELS_EN } = loaderData!.__d as typeof import("@/data/visa-compliance-topics");
    const url = `${SITE}/en/visa-compliance`;
    const title = `Thailand Visa Compliance Guide — TM30, 90-Day, Re-Entry, Overstay, TDAC | NYC`;
    const desc = `Complete Thailand immigration compliance guide for foreigners — TM30 residence notification, 90-day report (TM47), Re-entry Permit, Overstay penalties, TDAC arrival card, Certificate of Residence, in-country visa extension. Fees, deadlines, and legal references for 2026. Get your checklist in minutes.`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/visa-compliance", "en") }, { name: "description", content: ctaDesc(desc, "/en/visa-compliance", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/visa-compliance", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/visa-compliance", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
        { name: "keywords", content: "TM30 Thailand, 90 day report, TM47, re-entry permit Thailand, overstay fine, TDAC, Thailand visa extension, certificate of residence" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks("/visa-compliance", "/en/visa-compliance")],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "CollectionPage", "@id": `${url}#page`,
              name: title, description: desc, url, inLanguage: "en-US",
              mainEntity: {
                "@type": "ItemList", numberOfItems: COMPLIANCE_TOPICS.length,
                itemListElement: COMPLIANCE_TOPICS.map((t, i) => ({
                  "@type": "ListItem", position: i + 1, name: t.titleEn,
                  url: `${SITE}/en/visa-compliance/${t.slug}`,
                })),
              },
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: "Thailand Visa Compliance & Immigration Reporting Service",
              serviceType: "Thailand Visa Compliance & Immigration Reporting Service",
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
              areaServed: "TH",
              offers: { "@type": "Offer", price: "1500", priceCurrency: "THB", description: "TM30 / 90-day / TM28 filing service" },
            }),
          },
          faqPageSchema([
            { q: "What is the difference between TM30 and TM28?", a: "TM30 = homeowner notifies foreigner arrival within 24 hours. TM28 = foreigner notifies address change within 24 hours. Both must be filed together when relocating." },
            { q: "Can I file the 90-day report online?", a: "Yes, via tm47.immigration.go.th between days 75–90 of your stay (the 15-day pre-window) — except the very first report on each new visa entry, which requires one walk-in at Chaeng Wattana B Building or your local Immigration office for biometric re-registration. NYC files online on your behalf from THB 800; walk-ins are arranged with same-day queue tickets." },
            { q: "What happens if I leave Thailand without a re-entry permit?", a: "Your single-entry Non-Immigrant visa is automatically void the moment you cross the border, even by 1 hour. You must apply for a brand-new visa from a Thai embassy abroad (Vientiane, Penang, Phnom Penh typical) — no appeal, no in-country reinstatement. Single re-entry permit costs THB 1,000 and multiple THB 3,800 at any Immigration office in 30 minutes — always file before leaving." },
            { q: "Does a 1-day overstay get me blacklisted?", a: "No. Immigration Act B.E. 2522 §12(10) blacklists start at 90+ days of self-reported overstay (1-year ban) and scale to 10 years for 1+ year overstay. Any overstay caught at a police checkpoint or at the airport is detained, fined THB 500/day (cap THB 20,000), and triggers a 2–5 year ban on the spot — settle voluntarily at Immigration within 24 hours." },
            { q: "Is TDAC required every entry?", a: "Yes. Since 1 May 2025 the Thailand Digital Arrival Card (TDAC) replaces the paper TM6 entirely for every foreign entry — air, land or sea — including visa-exempt, Tourist, DTV, LTR, Non-O, Non-B and Elite holders. File free at tdac.immigration.go.th within 72 hours before arrival; QR code is scanned at the immigration counter." },
          ]),
        ]),
      ],
    };
  },
  component: ComplianceHubEn,
});

function ComplianceHubEn() {
  const { COMPLIANCE_TOPICS, COMPLIANCE_CATEGORY_LABELS_EN } = (Route.useLoaderData() as { __d: typeof import("@/data/visa-compliance-topics") }).__d;
  const byCat = COMPLIANCE_TOPICS.reduce<Record<string, typeof COMPLIANCE_TOPICS>>((acc, t) => {
    (acc[t.category] ||= []).push(t); return acc;
  }, {});
  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-16 pb-12">
        <div className="mx-auto max-w-6xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/en" className="hover:text-primary">Home</Link>
            <span className="mx-2">/</span><span>Visa Compliance</span>
          </nav>
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
            <Stamp className="mr-1 inline h-3 w-3" /> Immigration · Compliance · {COMPLIANCE_TOPICS.length} topics
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            Thailand Visa & Foreigner Compliance — Complete Guide
          </h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            TM30 · 90-Day Report (TM47) · Re-Entry Permit · TM28 · Overstay · TDAC · Certificate of Residence · Visa Extension — every step, fee, deadline, and legal reference, updated for 2026.
          </p>
          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">What every foreigner in Thailand must know about visa compliance?</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Five essentials: (1) TM30 — host files within 24 hours; (2) 90-Day Report — every 90 days, free online; (3) Re-entry Permit — required before leaving (THB 1,000/3,800); (4) TDAC — file online within 72 hours before arrival; (5) Avoid overstay (THB 500/day, 1–10 year blacklist). NYC has handled 8,000+ TM30/TM47/TM28/Extension cases.
            </p>
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> Call +66 92 242 9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
            <Link to="/en/visa" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <FileText className="h-4 w-4" /> All visa types
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">Choose a topic</h2>
          <p className="mt-2 text-sm text-muted-foreground">Grouped by legal category — click for steps, fees, deadlines, and documents.</p>
          <div className="mt-8 space-y-10">
            {(Object.keys(byCat) as ComplianceCategory[]).map((cat) => (
              <div key={cat}>
                <h3 className="mb-4 text-lg font-bold text-primary">{COMPLIANCE_CATEGORY_LABELS_EN[cat]}</h3>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {byCat[cat].map((t) => (
                    <Link
                      key={t.slug}
                      to="/en/visa-compliance/$topic"
                      params={{ topic: t.slug }}
                      className="group rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-0.5 hover:border-primary hover:shadow-md"
                      data-track-zone="compliance-hub-en"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{t.icon}</span>
                        <div className="font-bold text-sm">{t.titleEn}</div>
                      </div>
                      <div className="mt-2 line-clamp-2 text-xs text-muted-foreground">{t.taglineEn}</div>
                      <div className="mt-3 flex items-center justify-between text-xs">
                        <span className="rounded-full bg-muted px-2 py-0.5 font-semibold">{t.deadlineEn}</span>
                        <span className="inline-flex items-center gap-1 font-semibold text-primary">
                          View <ArrowRight className="h-3 w-3" />
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-amber-500/10 px-4 py-10">
        <div className="mx-auto max-w-5xl rounded-2xl border-2 border-amber-500/40 bg-card p-6">
          <h2 className="text-xl font-extrabold text-amber-700 dark:text-amber-400">
            <AlertTriangle className="mr-2 inline h-5 w-5" />Overstay & Blacklist Warning
          </h2>
          <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground">
            <li>• <strong>≤ 90 days</strong>: THB 500/day · cap THB 20,000 · no blacklist (self-report only)</li>
            <li>• <strong>90 days – 1 yr</strong>: 1-year blacklist · <strong>1–3 yrs</strong>: 3-year blacklist</li>
            <li>• <strong>3–5 yrs</strong>: 5-year blacklist · <strong>5–10 yrs</strong>: 10-year blacklist · <strong>10+ yrs</strong>: lifetime</li>
            <li>• Arrested inside Thailand: detention at Suan Phlu IDC + deportation + no fine cap</li>
          </ul>
          <Link to="/en/visa-compliance/$topic" params={{ topic: "overstay-fine" }} className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-4 py-2 text-sm font-bold text-white hover:bg-amber-600">
            <Clock className="h-4 w-4" /> Overstay details
          </Link>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl"><ShieldCheck className="mr-2 inline h-6 w-6 text-primary" />Related services</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { t: "DTV 5-year (Digital Nomad)", d: "Workcation · Freelancer · Muay Thai · 9 categories", to: "/en/dtv" as const },
              { t: "All visa types", d: "Non-B / LTR / DTV / Retirement / Marriage", to: "/en/visa" as const },
              { t: "Work Permit + Non-B", d: "15 nationalities · BOI fast-track", to: "/en/work-permit" as const },
              { t: "Driving license conversion", d: "12 nationalities · uses Certificate of Residence", to: "/en/driving-license" as const },
              { t: "Certified translation", d: "Passport, marriage cert, bank statements", to: "/en/translate" as const },
              { t: "Apostille / Legalisation", d: "60+ Hague + 16 non-Hague countries", to: "/en/apostille" as const },
              { t: "Notary Public", d: "POA, Affidavit, Embassy authentication", to: "/en/notary" as const },
            ].map((s) => (
              <Link key={s.t} to={s.to} className="rounded-xl border border-border bg-card p-4 hover:border-primary">
                <div className="font-bold">{s.t}</div>
                <div className="mt-1 text-xs text-muted-foreground">{s.d}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <TrustLayer variant="compact" />
    </main>
  );
}
